import { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Group, Modal, Paper, ScrollArea, Stack, Text, Title } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { usePlanoModeloList } from '../../hooks/usePlanosModelo'
import { useAplicarPlanoModelo, useAplicarPlanosEmLote } from '../../hooks/usePlanosPmoc'
import { extractApiError } from '../../lib/extractApiError'
import type { PMOC } from '../../types'
import type { PlanoModelo, PlanoPMOC } from '../../types/pmocPlanos'

interface Props {
  open: boolean
  onClose: () => void
  pmoc: PMOC
  planosAplicados?: PlanoPMOC[]
}

export default function AplicarPlanoModeloModal({ open, onClose, pmoc, planosAplicados = [] }: Props) {
  const [selecionados, setSelecionados] = useState<Record<string, string[]>>({})
  const catalogo = usePlanoModeloList({ ativo: true, page_size: 500 })
  const aplicarLote = useAplicarPlanosEmLote(pmoc.id)
  const aplicarUnico = useAplicarPlanoModelo(pmoc.id)

  useEffect(() => {
    if (!open) setSelecionados({})
  }, [open])

  const planosEquipamento = useMemo(
    () => (catalogo.data?.results ?? []).filter((plano) => plano.tipo_plano === 'equipamento'),
    [catalogo.data],
  )
  const planosAmbiente = useMemo(
    () => (catalogo.data?.results ?? []).filter((plano) => plano.tipo_plano === 'ambiente'),
    [catalogo.data],
  )
  const jaAplicados = useMemo(() => {
    const pares = new Set<string>()
    planosAplicados.forEach((plano) => {
      if (!plano.plano_modelo_origem) return
      plano.equipamentos.forEach((equipamento) => pares.add(`${equipamento}:${plano.plano_modelo_origem}`))
    })
    return pares
  }, [planosAplicados])

  function compativeis(tipo: string) {
    return planosEquipamento.filter((plano) => plano.tipos_equipamento.includes(tipo))
  }

  function sugestaoPara(equipamentoId: string, tipo: string): PlanoModelo | undefined {
    const candidatos = compativeis(tipo)
    return candidatos.find((p) => !jaAplicados.has(`${equipamentoId}:${p.id}`))
  }

  function toggle(equipamentoId: string, planoId: string) {
    setSelecionados((atual) => {
      const lista = new Set(atual[equipamentoId] ?? [])
      if (lista.has(planoId)) lista.delete(planoId)
      else lista.add(planoId)
      return { ...atual, [equipamentoId]: Array.from(lista) }
    })
  }

  function payloadDasSelecoes(mapa: Record<string, string[]>) {
    const porPlano = new Map<string, string[]>()
    Object.entries(mapa).forEach(([equipamento, planos]) => {
      planos.forEach((plano) => porPlano.set(plano, [...(porPlano.get(plano) ?? []), equipamento]))
    })
    return Array.from(porPlano, ([plano_modelo, equipamentos]) => ({ plano_modelo, equipamentos }))
  }

  async function aplicar(mapa: Record<string, string[]>) {
    const selecoes = payloadDasSelecoes(mapa)
    if (selecoes.length === 0) {
      notifications.show({ color: 'orange', message: 'Selecione ao menos um plano.' })
      return
    }
    try {
      const resultado = await aplicarLote.mutateAsync({ selecoes })
      const total = resultado.criados.reduce((soma, plano) => soma + plano.equipamentos.length, 0)
      notifications.show({
        color: 'green',
        message: `${total} vínculo(s) equipamento-plano aplicados.${resultado.ignorados_ja_aplicados ? ` ${resultado.ignorados_ja_aplicados} já existiam.` : ''}`,
      })
      setSelecionados({})
      onClose()
    } catch (err) {
      notifications.show({ color: 'red', title: 'Falha ao aplicar planos', message: extractApiError(err, 'Confira a compatibilidade das escolhas.') })
    }
  }

  function selecionarTodasSugestoes() {
    const sugestoes: Record<string, string[]> = {}
    ;(pmoc.equipamentos ?? []).forEach((equipamento) => {
      const plano = sugestaoPara(equipamento.id, equipamento.tipo)
      if (plano) sugestoes[equipamento.id] = [plano.id]
    })
    setSelecionados(sugestoes)
    const quantidade = Object.values(sugestoes).reduce((total, planos) => total + planos.length, 0)
    notifications.show({
      color: 'blue',
      message: `${quantidade} sugestão(ões) selecionada(s). Revise e clique em "Aplicar selecionados" para confirmar.`,
    })
  }

  async function aplicarPlanoAmbiente(plano: PlanoModelo) {
    if (!pmoc.cliente) return
    try {
      await aplicarUnico.mutateAsync({ plano_modelo: plano.id, ambientes: [], site: pmoc.cliente })
      notifications.show({ color: 'green', message: `Plano "${plano.nome}" aplicado ao cliente do PMOC.` })
      onClose()
    } catch (err) {
      notifications.show({ color: 'red', message: extractApiError(err, 'Não foi possível aplicar o plano de ambiente.') })
    }
  }

  const quantidadeSelecionada = Object.values(selecionados).reduce((soma, itens) => soma + itens.length, 0)
  const semSugestao = (pmoc.equipamentos ?? []).filter((eq) => compativeis(eq.tipo).length === 0).length

  return (
    <Modal opened={open} onClose={onClose} title="Aplicar planos por equipamento" size="xl" centered>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Text size="sm" c="dimmed" maw={600}>
            Clique nas etiquetas para escolher planos. Planos já aplicados aparecem marcados e não serão duplicados.
          </Text>
          <Button onClick={selecionarTodasSugestoes} variant="light">
            Selecionar sugestões
          </Button>
        </Group>

        {semSugestao > 0 && (
          <Alert color="orange">{semSugestao} equipamento(s) ainda não possuem plano compatível no catálogo.</Alert>
        )}

        <ScrollArea h={430} offsetScrollbars>
          <Stack gap="xs">
            {(pmoc.equipamentos ?? []).map((equipamento) => {
              const planos = compativeis(equipamento.tipo)
              return (
                <Paper key={equipamento.id} p="sm" withBorder>
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <div style={{ minWidth: 230 }}>
                      <Text fw={600}>{equipamento.marca} {equipamento.modelo}</Text>
                      <Text size="xs" c="dimmed">{equipamento.tipo_display ?? equipamento.tipo.replace(/_/g, ' ')}</Text>
                    </div>
                    <Group gap="xs" justify="flex-end">
                      {planos.length === 0 ? (
                        <Text size="xs" c="orange">Sem plano compatível</Text>
                      ) : planos.map((plano) => {
                        const aplicado = jaAplicados.has(`${equipamento.id}:${plano.id}`)
                        const selecionado = selecionados[equipamento.id]?.includes(plano.id) ?? false
                        return (
                          <Badge
                            key={plano.id}
                            component="button"
                            type="button"
                            size="lg"
                            variant={aplicado || selecionado ? 'filled' : 'light'}
                            color={aplicado ? 'green' : selecionado ? 'blue' : 'gray'}
                            disabled={aplicado}
                            onClick={() => toggle(equipamento.id, plano.id)}
                            style={{ cursor: aplicado ? 'default' : 'pointer' }}
                            title={`${plano.nome} — ${plano.atividades.length} atividades`}
                          >
                            {aplicado ? '✓ ' : ''}{plano.nome}
                          </Badge>
                        )
                      })}
                    </Group>
                  </Group>
                </Paper>
              )
            })}
          </Stack>
        </ScrollArea>

        {planosAmbiente.length > 0 && (
          <Paper p="sm" withBorder>
            <Title order={5} mb="xs">Planos de ambiente</Title>
            <Group gap="xs">
              {planosAmbiente.map((plano) => (
                <Button key={plano.id} size="xs" variant="light" onClick={() => aplicarPlanoAmbiente(plano)} loading={aplicarUnico.isPending}>
                  Aplicar {plano.nome}
                </Button>
              ))}
            </Group>
          </Paper>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancelar</Button>
          <Button disabled={quantidadeSelecionada === 0} loading={aplicarLote.isPending} onClick={() => aplicar(selecionados)}>
            Aplicar selecionados ({quantidadeSelecionada})
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
